import {
  Checkbox,
  Divider,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Text,
  VStack,
} from "@chakra-ui/react";
import { UserX, X } from "lucide-react";
import { memo, useEffect, useMemo, useState } from "react";
import { Button } from "components/ds";
import {
  orderedPersonIdentities,
  personIdentityLabel,
  personDecisionTargetIds,
  type FrozenAccountPredicate,
  type MFPersonReviewRow,
} from "../reviewLogic";
import { IdentityAvatar } from "./AccountRow";

interface RevokeScopeModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Display name of the person the revoke targets. */
  personName: string;
  /** The chosen outcome's display name, e.g. "Not Required". */
  actionName: string;
  row: MFPersonReviewRow | null;
  /** Optional account-id restriction (the card-scoped table's filter). */
  scope?: string[];
  /**
   * Identities whose outcome already started de-provisioning; never offered
   * on a re-decision (PLO-5749).
   */
  isFrozen?: FrozenAccountPredicate;
  /** Proceed with the chosen account ids into the normal decision flow. */
  onConfirm: (accountIds: string[]) => void;
}

/**
 * RevokeScopeModal (PLO-5424) — a revoke on a person with several identities
 * must say which identities it covers. Offers "Revoke all" and a per-identity
 * chooser; the chosen account ids continue into the regular decision modal,
 * so notes and the deprovision consent gate still apply. Single-identity
 * rows never see this modal.
 */
const RevokeScopeModal = ({
  isOpen,
  onClose,
  personName,
  actionName,
  row,
  scope,
  isFrozen,
  onConfirm,
}: RevokeScopeModalProps) => {
  const [mode, setMode] = useState<"all" | "choose">("all");
  const [chosenIds, setChosenIds] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen) {
      setMode("all");
      setChosenIds([]);
    }
  }, [isOpen]);

  const eligibleIds = useMemo(
    () => (row ? personDecisionTargetIds(row, scope, isFrozen) : []),
    [row, scope, isFrozen],
  );
  const identities = useMemo(
    () => (row ? orderedPersonIdentities(row) : []),
    [row],
  );

  if (!row) return null;

  const toggleId = (accountId: string, checked: boolean) => {
    setChosenIds((prev) =>
      checked ? [...prev, accountId] : prev.filter((id) => id !== accountId),
    );
  };

  const confirmIds = mode === "all" ? eligibleIds : chosenIds;
  const canConfirm = confirmIds.length > 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay bg="rgba(0, 0, 0, 0.3)" backdropFilter="blur(3px)" />
      <ModalContent borderRadius="12px" boxShadow="lg">
        <ModalHeader
          fontSize="md"
          fontWeight="500"
          color="text.body.primary"
          pb={2}
          borderBottom="1px solid"
          borderColor="border.default"
        >
          <HStack spacing="sm" align="center">
            <Icon as={UserX} boxSize="16px" color="text.body.secondary" />
            <VStack align="flex-start" spacing={0} minW={0}>
              <Text>{`${actionName}: choose identities`}</Text>
              <Text
                fontSize="xs"
                fontWeight="400"
                color="text.body.tertiary"
                noOfLines={1}
              >
                {personName} has {identities.length} identities in this review
              </Text>
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton size="sm" top="12px" right="12px" />

        <ModalBody py={4} px={4}>
          <RadioGroup
            value={mode}
            onChange={(value) => setMode(value as "all" | "choose")}
          >
            <VStack align="stretch" spacing={3}>
              <Radio value="all" size="sm">
                <Text fontSize="sm" fontWeight="500">
                  Revoke all ({eligibleIds.length}{" "}
                  {eligibleIds.length === 1 ? "identity" : "identities"})
                </Text>
              </Radio>
              <Radio value="choose" size="sm">
                <Text fontSize="sm" fontWeight="500">
                  Choose identities
                </Text>
              </Radio>
            </VStack>
          </RadioGroup>

          {mode === "choose" && (
            <VStack
              align="stretch"
              spacing={0}
              mt={3}
              borderWidth="1px"
              borderColor="border.default"
              borderRadius="8px"
              overflow="hidden"
            >
              {identities.map((identity) => {
                const label = personIdentityLabel(identity, row.resource);
                const selectable = eligibleIds.includes(identity.accountId);
                return (
                  <Checkbox
                    key={identity.id}
                    size="sm"
                    isChecked={chosenIds.includes(identity.accountId)}
                    isDisabled={!selectable}
                    onChange={(e) =>
                      toggleId(identity.accountId, e.target.checked)
                    }
                    px={3}
                    py={2}
                    _notLast={{
                      borderBottom: "1px solid",
                      borderColor: "border.subtle",
                    }}
                  >
                    <HStack spacing="sm" align="center" minW={0}>
                      <IdentityAvatar providerKey={label.provider.key} />
                      <VStack align="flex-start" spacing="2xs" minW={0}>
                        <HStack spacing="6px" minW={0}>
                          <Text
                            fontSize="xs"
                            fontWeight="600"
                            color="text.body.primary"
                            noOfLines={1}
                          >
                            {label.provider.name}
                            {label.authTypeLabel
                              ? ` · ${label.authTypeLabel}`
                              : ""}
                            {label.mfaEnabled ? " · MFA" : ""}
                          </Text>
                          {label.isPrimary && (
                            <Text
                              fontSize="2xs"
                              fontWeight="600"
                              color="p.700"
                              bg="p.50"
                              px="6px"
                              py="1px"
                              borderRadius="full"
                              whiteSpace="nowrap"
                            >
                              Primary
                            </Text>
                          )}
                          {!selectable && (
                            <Text
                              fontSize="2xs"
                              fontWeight="500"
                              color="text.body.tertiary"
                              whiteSpace="nowrap"
                            >
                              Already decided
                            </Text>
                          )}
                        </HStack>
                        <Text
                          fontSize="xs"
                          color="text.body.tertiary"
                          fontFamily="mono"
                          noOfLines={1}
                        >
                          {label.value}
                        </Text>
                      </VStack>
                    </HStack>
                  </Checkbox>
                );
              })}
            </VStack>
          )}
        </ModalBody>

        <Divider borderColor="border.default" />

        <ModalFooter pt={3} pb={3}>
          <HStack spacing={2}>
            <Button
              variant="subtle"
              tone="neutral"
              size="md"
              onClick={onClose}
              leadingIcon={<Icon as={X} boxSize="14px" />}
            >
              Cancel
            </Button>
            <Button
              variant="solid"
              tone="error"
              size="md"
              onClick={() => onConfirm(confirmIds)}
              isDisabled={!canConfirm}
            >
              Continue
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default memo(RevokeScopeModal);
